import type { LibraryBlock } from "../build";
import { ellipse, motion, rect, text, WHITE } from "./helpers";

/** Titles with text effects and curved text (scene/text-effects.ts). */
export const EFFECT_TITLES: LibraryBlock[] = [
  {
    id: "titel-neon",
    title: "Neon-Titel",
    category: "titel",
    tags: ["neon", "leuchten", "effekt", "party", "nacht"],
    width: 960,
    height: 300,
    anchor: "center",
    duration: 3500,
    nodes: [
      rect({
        x: 0,
        y: 0,
        w: 960,
        h: 300,
        fill: "#0b0b1a",
        radius: "md",
        name: "Fläche",
        ...motion("fade"),
      }),
      text({
        text: "NEON NIGHTS",
        style: "heading-1",
        size: 110,
        color: "#ff5ce1",
        effect: { type: "neon", intensity: 60 },
        x: 40,
        y: 0,
        w: 880,
        h: 300,
        align: "center",
        ...motion("fade", { delay: 150, duration: 700, loop: "pulse" }),
      }),
    ],
  },
  {
    id: "titel-kreis-sale",
    title: "Rundes Sale-Siegel",
    category: "titel",
    tags: ["sale", "rabatt", "kreis", "rund", "gebogen", "siegel", "badge"],
    width: 440,
    height: 440,
    anchor: "center",
    duration: 3500,
    nodes: [
      ellipse({
        x: 135,
        y: 135,
        w: 170,
        h: 170,
        fill: "accent",
        name: "Kreis",
        ...motion("pop"),
      }),
      text({
        text: "-30 %",
        style: "heading-1",
        size: 54,
        color: "on:accent",
        x: 135,
        y: 135,
        w: 170,
        h: 170,
        align: "center",
        ...motion("pop", { delay: 120 }),
      }),
      text({
        text: "JETZT • SALE • JETZT • SALE • ",
        style: "subline",
        size: 52,
        color: "accent",
        curve: 100,
        x: 0,
        y: 0,
        w: 440,
        h: 440,
        align: "center",
        ...motion("spin", { duration: 900 }),
      }),
    ],
  },
  {
    id: "titel-bogen",
    title: "Titel im Bogen",
    category: "titel",
    tags: ["gebogen", "bogen", "kurve", "rund"],
    width: 960,
    height: 280,
    anchor: "top",
    duration: 3500,
    nodes: [
      text({
        text: "Willkommen zu Hause",
        style: "heading-1",
        size: 92,
        color: "accent",
        curve: 30,
        x: 0,
        y: 0,
        w: 960,
        align: "center",
        ...motion("pop"),
      }),
    ],
  },
  {
    id: "titel-laecheln",
    title: "Lächelnder Bogen",
    category: "titel",
    tags: ["gebogen", "bogen", "kurve", "lächeln"],
    width: 900,
    height: 260,
    anchor: "bottom",
    duration: 3500,
    nodes: [
      text({
        text: "gute Laune garantiert",
        style: "subline",
        size: 70,
        color: "text",
        curve: -35,
        x: 0,
        y: 0,
        w: 900,
        align: "center",
        ...motion("slide-up"),
      }),
    ],
  },
  {
    id: "titel-hohl",
    title: "Hohler Titel",
    category: "titel",
    tags: ["hohl", "umriss", "outline", "effekt"],
    width: 980,
    height: 260,
    anchor: "center",
    duration: 3500,
    nodes: [
      text({
        text: "NEU",
        style: "heading-1",
        size: 220,
        color: "accent",
        effect: { type: "hollow", thickness: 35 },
        x: 0,
        y: 0,
        w: 980,
        align: "center",
        ...motion("zoom"),
      }),
    ],
  },
  {
    id: "titel-echo",
    title: "Echo-Titel",
    category: "titel",
    tags: ["echo", "wiederholung", "retro", "effekt"],
    width: 960,
    height: 240,
    anchor: "center",
    duration: 3500,
    nodes: [
      text({
        text: "Jetzt live",
        style: "heading-1",
        size: 130,
        color: "accent",
        effect: { type: "echo", offset: 35, direction: 45 },
        x: 0,
        y: 0,
        w: 960,
        align: "center",
        ...motion("slide-right"),
      }),
    ],
  },
  {
    id: "titel-versatz",
    title: "Glitch-Titel",
    category: "titel",
    tags: ["glitch", "versatz", "gaming", "effekt"],
    width: 960,
    height: 240,
    anchor: "center",
    duration: 3500,
    nodes: [
      text({
        text: "GAME ON",
        style: "heading-1",
        size: 140,
        color: WHITE,
        effect: { type: "glitch", offset: 40, direction: 0, colors: ["#00e5ff", "#ff2bd6"] },
        x: 0,
        y: 0,
        w: 960,
        align: "center",
        ...motion("pop", { loop: "wiggle" }),
      }),
    ],
  },
  {
    id: "titel-markiert",
    title: "Markierter Titel",
    category: "titel",
    tags: ["markiert", "hintergrund", "textmarker", "effekt"],
    width: 900,
    height: 260,
    anchor: "center",
    duration: 3500,
    nodes: [
      text({
        text: "Das Wichtigste\nzuerst",
        style: "heading-1",
        size: 88,
        color: "on:accent",
        effect: { type: "highlight", color: "#ffe066", padding: 30, radius: 25, opacity: 1 },
        effectColor: "accent",
        x: 40,
        y: 0,
        w: 820,
        align: "center",
        ...motion("slide-up"),
      }),
    ],
  },
  {
    id: "titel-verlauf",
    title: "Verlaufs-Titel",
    category: "titel",
    tags: ["verlauf", "farbverlauf", "gradient", "effekt"],
    width: 960,
    height: 240,
    anchor: "center",
    duration: 3500,
    nodes: [
      text({
        text: "Sommer-Sale",
        style: "heading-1",
        size: 130,
        effect: {
          type: "gradient",
          fill: {
            type: "linear",
            angle: 90,
            stops: [
              { offset: 0, color: "#f97316" },
              { offset: 1, color: "#db2777" },
            ],
          },
        },
        x: 0,
        y: 0,
        w: 960,
        align: "center",
        ...motion("zoom"),
      }),
    ],
  },
];
